import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { OnHandLogo } from '../components/brand/OnHandLogo';
import { supabase, BUILD } from '../lib/supabase';
import ShipLabel from './signin/ShipLabel';
import useConveyor from './signin/useConveyor';
import '../styles/brand-signin.css';

/*
  The sign-in screen.

  **Rebuilt 11 September 2026 to the brand guide's sections 9.9 and 14.4**, job
  27 phase 4. Before that it was the design the owner approved on 28 August
  2026, whose drawings are in docs/design/login/. The guide is newer than that
  approval and wins, which is his own decision of 11 September.

  What the guide changed, in its own order: the full-bleed artwork instead of a
  repeating tile, a glass card instead of a solid white one, "Welcome back."
  with a line under it instead of a capitals label, sentence case field labels
  instead of small capitals, a Show and Hide button inside the password field,
  the wording of the error, and "Forgot password?" centred instead of tucked
  against the right edge.

  **Everything it does is what it did.** Nothing here touches sign-in itself,
  the reset email, the session, or what happens to somebody thrown out. Phase 4
  is styling, branding and copy.

  **Rebrand step 7, 17 September 2026: the conveyor.** The screen is a live 3D
  warehouse now (`signin/conveyorScene.js`, ported from the brand's approved
  reference), the card arrives out of a cardboard box, a successful sign-in puts
  it back into one, and a shipping label says what happened. **None of the
  signing in changed**: the same `signIn`, the same reset email, the same
  messages for a deactivated account and for anything else that goes wrong. The
  request starts the moment the button is pressed and the animation plays
  alongside it, so nobody waits on the picture (guide 9.1 and 9.6).

  This screen has no shell, and that is deliberate rather than unfinished. The
  bar, the menu and the dock all describe a person to themselves, and nobody is
  known yet on the way in, so there is nothing to put in them. Rule 24.

  **One size of everything now.** The card used to scale its type and spacing
  down by 8 percent below 700px. The guide gives the phone a padding instead
  (28px 24px against 40px 40px 32px) and leaves the type alone, which is both
  simpler and a bigger touch target, so the scaling helper is gone.
*/

/*
  **The home page rides in on the conveyor** (guide 9.6), 18 September 2026.
  The app is drawn underneath the sign-in page when the sweep starts (see
  `AppRoutes` in App.jsx), so everything before this page in the document is
  the app. It starts off the right-hand edge, rides in over the departing
  sign-in page 220ms after it starts to leave, and settles with a small bump,
  timed from docs/brand/reference/onhand-signin-approved-v1.html's
  `sweepToHome` (the bump follows the ride rather than overlapping its last
  120ms). Reduced motion fades it in instead, as the reference does.

  **The home page redraws itself a moment after it first appears**, so the
  pieces there when the sweep starts are not the pieces on screen a frame
  later. Found by driving a real sign-in in Chrome: the first version animated
  the pieces it found, and the ones that replaced them simply sat there. So for
  as long as the ride lasts, every piece added before this page is put on the
  same animation at the same point in it, and the whole app moves as one.

  Every piece is lifted to the same layer (4, above the sign-in page's 0) and
  put back when the ride ends, so the app's own layering inside is untouched.

  **And each moving piece is held to the size of the screen while it rides.**
  A moving piece carries its fixed parts with it, measured from its own edges,
  and the app is taller than the screen: the phone's tab bar, which belongs at
  the bottom of the screen, was at the bottom of the whole home page, out of
  sight, and popped in when the ride ended. Pinned to the screen for the ride,
  the tab bar rides in where it belongs. A signed-in page opens at the top, so
  nothing else about what shows changes.
*/
const RIDE_MS = 220 + 900 + 420;
const RIDE = [
  { transform: 'translateX(110vw)', offset: 0 },
  { transform: 'translateX(110vw)', offset: 220 / RIDE_MS, easing: 'cubic-bezier(.16, 1, .3, 1)' },
  { transform: 'translateX(0)', offset: 1120 / RIDE_MS, easing: 'cubic-bezier(.3, .6, .4, 1)' },
  { transform: 'translateX(-14px)', offset: (1120 + 147) / RIDE_MS, easing: 'cubic-bezier(.3, .6, .4, 1)' },
  { transform: 'translateX(4px)', offset: (1120 + 294) / RIDE_MS, easing: 'cubic-bezier(.3, .6, .4, 1)' },
  { transform: 'translateX(0)', offset: 1 },
];

function bringHomeIn(pageEl) {
  const parent = pageEl && pageEl.parentElement;
  if (!parent || !pageEl.animate) return;
  const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const keyframes = reduce ? [{ opacity: 0 }, { opacity: 1 }] : RIDE;
  const timing = reduce ? { duration: 300, easing: 'ease' } : { duration: RIDE_MS, fill: 'backwards' };
  // One moment every piece's ride is measured from, however late it arrives.
  const startTime = document.timeline ? document.timeline.currentTime : null;
  const done = new WeakSet();

  const ride = el => {
    if (!(el instanceof HTMLElement) || el === pageEl || done.has(el) || !el.animate) return;
    done.add(el);
    const cs = getComputedStyle(el);
    if (cs.display === 'none') return;
    const PINNED = ['position', 'top', 'right', 'bottom', 'left', 'overflow', 'zIndex'];
    const before = {};
    PINNED.forEach(k => { before[k] = el.style[k]; });
    if (cs.position !== 'fixed') Object.assign(el.style, { position: 'fixed', top: '0', right: '0', bottom: '0', left: '0', overflow: 'hidden' });
    el.style.zIndex = '4';
    const restore = () => { PINNED.forEach(k => { el.style[k] = before[k]; }); };
    const anim = el.animate(keyframes, timing);
    // Pieces that arrive late join the ride where the first ones already are.
    if (startTime !== null) anim.startTime = startTime;
    anim.onfinish = restore;
    anim.oncancel = restore;
  };
  const beforePage = el => !pageEl.isConnected || !!(el.compareDocumentPosition(pageEl) & Node.DOCUMENT_POSITION_FOLLOWING);

  for (let el = pageEl.previousElementSibling; el; el = el.previousElementSibling) ride(el);
  const watch = new MutationObserver(records => records.forEach(r => r.addedNodes.forEach(n => { if (n.nodeType === 1 && beforePage(n)) ride(n); })));
  watch.observe(parent, { childList: true });
  setTimeout(() => watch.disconnect(), (reduce ? 300 : RIDE_MS) + 50);
}

export default function LoginPage({ onSequence } = {}) {
  /*
    **Where the sign-in sequence is, told to App.jsx** (18 September 2026):
    'playing' from the moment a sign-in is sent, 'leaving' for the sweep, and
    'idle' otherwise. The app keeps this page on screen until it hears
    'leaving', so the box, the label and the sweep all get to play.
  */
  const sequenceRef = useRef(onSequence);
  sequenceRef.current = onSequence;
  const sequence = phase => { if (sequenceRef.current) sequenceRef.current(phase); };
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [reveal, setReveal] = useState(false);

  /*
    **Why you were thrown out. The sign-in screen's sixth state.**

    Built 10 September 2026 for job 25, and it is the owner's own answer: asked
    whether the sign-in screen should say why somebody was signed out
    mid-session, he said "yes."

    **Until that day the reason went to the audit log and nowhere else.**
    Somebody who stepped away for lunch came back to a sign-in screen with
    nothing on it, which looks exactly the same as being signed out for a fault,
    or as never having signed in at all, or as OnHand losing their work. The one
    thing they could not tell is the one thing that actually happened.

    **It is read once and then cleared**, so it explains the sign-in it belongs
    to and is gone the moment they sign in or reload. `AuthContext` writes it
    into `sessionStorage`, which the browser empties when the tab closes, so it
    can never turn up in front of somebody arriving fresh.
  */
  const [thrownOut] = useState(() => {
    try {
      const why = sessionStorage.getItem('onhand.signedOutBecause');
      if (why) sessionStorage.removeItem('onhand.signedOutBecause');
      return why;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  /*
    **The screen's own moving parts** (guide 9.5 to 9.8). `label` is the
    shipping label that slaps down, `invalid` is the red on both fields, and
    `sweeping` is the page riding off to the left on the belt.
  */
  const [label, setLabel] = useState(null);      // 'success' | 'error' | null
  const [muted, setMuted] = useState(false);     // the card behind a failure label
  const [jostled, setJostled] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const pageRef = useRef(null);
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  const timers = useRef([]);
  const later = (ms, fn) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  /*
    **The still picture hands over to this page.** public/index.html puts the
    warehouse behind the whole page before the app has loaded, for anybody with
    no saved session (18 September 2026). The canvas below carries the same
    picture in the same place, so once this page is drawn the early one goes,
    and no other screen ever finds it behind it.
  */
  useEffect(() => { document.documentElement.classList.remove('oh-signin-boot'); }, []);

  const signedIn = useRef(false);
  const boxClosed = useRef(false);
  const labelled = useRef(false);
  const swept = useRef(false);

  const conveyor = useConveyor({
    pageRef, cardRef, canvasRef,
    onSuccessBoxClosed: () => { boxClosed.current = true; finishIfReady(); },
  });

  /*
    **The sign-in sequence finishes when both halves are done** (guide 9.6): the
    box has closed on the card, and the real request has come back. Whichever
    lands last starts the label, and 1.7 seconds later the page rides away.
  */
  function finishIfReady() {
    if (!signedIn.current || !boxClosed.current || labelled.current) return;
    labelled.current = true;
    setLabel('success');
    later(1700, sweepToHome);
  }

  /*
    **The sweep** (guide 9.6). The address becomes the home page first, so the
    app is drawn underneath on the page it will stay on, and nothing swaps
    under it as it rides in. Until 18 September this navigated at the END of
    the sweep, which only ever worked because nothing was underneath.
  */
  function sweepToHome() {
    if (swept.current) return;
    swept.current = true;
    navigate('/');
    sequence('leaving');
    setSweeping(true);
    later(820, () => {
      sequence('idle');
      /*
        Normally the app has taken this page away by now. If it is still here,
        the app decided nobody is signed in after all, so put the page back the
        way it was rather than leave it swept off the screen.
      */
      later(100, () => {
        setSweeping(false); setLabel(null);
        signedIn.current = false; boxClosed.current = false;
        labelled.current = false; swept.current = false;
        conveyor.cancelSuccess();
      });
    });
  }

  // Before the paint, or the home page shows for one frame where it will end up.
  useLayoutEffect(() => { if (sweeping) bringHomeIn(pageRef.current); }, [sweeping]);

  /* A failed sign-in (guide 9.7). */
  /*
    The label stays up for three seconds, long enough to read, then the card
    comes back with the error written on it. The guide's 9.7 says 1.7; the
    owner chose three on 18 September 2026, after watching it on the live site.
  */
  const FAILURE_LABEL_MS = 3000;
  function failSequence() {
    conveyor.jam();
    setInvalid(true);
    setLabel('error');
    setMuted(true);
    later(FAILURE_LABEL_MS, () => {
      setLabel(null);
      setMuted(false);
      later(300, () => { setJostled(true); later(560, () => setJostled(false)); });
    });
  }

  const [showForgot, setShowForgot] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  /*
    **Where the link comes back to.** `window.location.origin`, so a link sent
    from the live site returns to the live site and one sent from anywhere else
    returns there. Writing the address in would mean changing code the next time
    it moves, and it moved on 10 September 2026 when the owner's own domain
    arrived.

    **The address has to be on Supabase's list of allowed return addresses** or
    the link lands on the sign-in screen with nothing said. That list is in the
    Supabase dashboard, not in this repository, and it is in the setup notes.
  */
  async function sendReset() {
    setError(null);
    setSending(true);
    const address = email.trim();
    let err = null;
    try {
      ({ error: err } = await supabase.auth.resetPasswordForEmail(address, {
        redirectTo: window.location.origin + '/reset',
      }));
    } catch (e) {
      err = e;
    }
    setSending(false);
    /*
      **A refused SEND is said out loud; an unknown ADDRESS is not.** They are
      different things. Supabase answers the same either way for an address it
      does not know, on purpose, so the only errors that reach here are real
      ones: no connection, or the provider refusing. Swallowing those would leave
      somebody waiting for an email that was never sent.
    */
    if (err) {
      setError({ tone: 'wrong', text: 'That could not be sent just now. Try again in a moment.' });
      return;
    }
    setSentTo(address);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading || sweeping || signedIn.current) return;
    setError(null);
    setInvalid(false);
    /*
      **The page checks the two fields itself** (guide 9.7), because the
      browser's own validation is turned off on the form: a bubble from the
      browser stops the animation dead and says nothing in OnHand's voice.
    */
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || password.length === 0) {
      setError({ tone: 'wrong', text: password.length === 0 && email.trim()
        ? 'Enter your password.'
        : 'Enter your work email address and your password.' });
      failSequence();
      return;
    }
    setLoading(true);
    sequence('playing');
    /*
      **The box waits for the answer**, 18 September 2026, on the owner's word:
      a wrong password gets the label and nothing else, never a box coming to
      take the card away. Nothing can know the answer before it arrives, so the
      button says "Signing in..." until it does, and only a yes starts the box.

      Until then the box started the moment the button was pressed, as the
      guide's 9.6 asks, and a refusal meant the card had already gone into the
      box. The scene never gave it back, so after the label the card was simply
      gone and there was nothing left to type into; the owner filmed it. (Before
      that morning's fix to how the card is hidden, React was putting the card
      back by accident, which is why nobody had seen it.)
    */
    let ok = false, failed = null;
    try { await signIn(email, password); ok = true; } catch (err) { failed = err; }
    setLoading(false);
    if (ok) {
      signedIn.current = true;
      conveyor.success(() => true);
      finishIfReady();
      /*
        **Never held for ever.** The box closing is what starts the label and
        the sweep; if it never reports in, the person is signed in all the
        same, so after eight seconds the sweep goes regardless.
      */
      later(8000, sweepToHome);
      return;
    }
    sequence('idle');
    {
      const err = failed;
      /*
        **A wrong password and an unknown email get the same words on purpose.**
        Saying which one was wrong tells a stranger whether an address is real.

        The wording is the guide's own, from its 9.9. It replaced "That email
        and password do not match. Please try again.", which said the same thing
        and blamed the person for it.

        **A deactivated account is different and gets its own wording AND its own
        colour.** The person knows their password is right, so telling them to
        check it sends them hunting for a problem that is not theirs. It is amber
        rather than red because it is a state rather than a mistake.

        **And anything else gets the real words, not the standard ones.** Until 7
        September 2026 every failure that was not a deactivated account was
        reported as a wrong password, including failures that had nothing to do
        with one, and the owner spent an evening being told his password was
        wrong when it was right every single time. That split stays exactly as it
        was: the guide gave this screen one sentence, and one sentence for every
        possible failure is the fault that evening was made of.
      */
      setError(err?.code === 'ACCOUNT_INACTIVE'
        ? { tone: 'state', text: err.message }
        : err?.message && !/invalid login credentials/i.test(err.message)
          ? { tone: 'state', text: err.message }
          : { tone: 'wrong', text: "That email and password don't match. Try again." });
      // The typed email stays, which the guide asks for by name. Only the
      // password is cleared.
      setPassword('');
      failSequence();
    }
  }

  return (
    <div
      ref={pageRef}
      className={'login-page' + (sweeping ? ' is-sweeping' : '') + (conveyor.state === 'loading' ? ' signin-hide-card' : '')}
      data-build={BUILD}
    >
      {/* The warehouse. It draws nothing until the library has loaded, and the
          card works whether it ever does or not (guide 9.9). */}
      <canvas ref={canvasRef} className="signin-scene" aria-hidden="true" />
      <div className="signin-vignette" aria-hidden="true" />

      {label && <ShipLabel kind={label} shown />}

      <div ref={cardRef} className={'signin-card' + (muted ? ' is-fading is-muted' : '') + (jostled ? ' is-jostled' : '')}>
        <div className="signin-logo">
          <OnHandLogo iconSize={40} animate="launch" />
        </div>

        <div className="signin-head">
          <h1 className="signin-title">Welcome back.</h1>
          <p className="signin-sub">Sign in to keep your supplies moving.</p>
        </div>

        {/*
          **The reason comes second to anything they just did.** If they have
          just typed a wrong password, that is the message that matters; being
          told why they were signed out twenty minutes ago is history by then.
        */}
        {!error && thrownOut && (
          <div className="signin-msg signin-msg-state" role="status">
            <div className="signin-msg-title">You were signed out</div>
            {thrownOut === 'SESSION_TIMEOUT'
              ? 'OnHand signs you out after two hours of nothing happening. Nothing you had saved was lost.'
              : thrownOut === 'ACCOUNT_INACTIVE'
                ? 'This account has been switched off. An administrator can turn it back on.'
                : 'Your session ended. Nothing you had saved was lost.'}
          </div>
        )}

        {error && (
          <div className={'signin-msg signin-msg-' + error.tone} role="alert">
            {error.text}
          </div>
        )}

        {/*
          **Both fields take the red, neither one alone** (guide 9.7, rebrand
          step 7). A wrong password and an unknown email still get the same
          words on purpose, so that a stranger cannot stand at this screen and
          find out whether an address is real; marking BOTH gives that away no
          more than the sentence does, and it is what the guide draws. Marking
          one would give it away, and that is still never done. The red clears
          the moment either field is typed in.

          The banner carries `role="alert"`, so a screen reader announces it the
          moment it appears.
        */}
        {/* `noValidate`: the page checks the two fields itself (guide 9.7), so
            a browser bubble never stops the sequence. */}
        <form className="signin-form" onSubmit={handleSubmit} noValidate>
          <div className="signin-fields">
            <div className="signin-field">
              <label className="fld-label" htmlFor="signin-email">Work email</label>
              <input
                id="signin-email" className="fld" type="email"
                placeholder="you@company.com" value={email} autoComplete="username"
                aria-invalid={invalid ? 'true' : undefined}
                onChange={e => { setEmail(e.target.value); if (invalid) setInvalid(false); }}
                onFocus={() => conveyor.setTyping(true)}
                onBlur={() => conveyor.setTyping(false)}
                required
              />
            </div>

            <div className="signin-field">
              <label className="fld-label" htmlFor="signin-password">Password</label>
              <div className="signin-pw">
                <input
                  id="signin-password" className="fld"
                  type={reveal ? 'text' : 'password'}
                  placeholder="Enter your password" value={password} autoComplete="current-password"
                  aria-invalid={invalid ? 'true' : undefined}
                  onChange={e => { setPassword(e.target.value); if (invalid) setInvalid(false); }}
                  onFocus={() => conveyor.setTyping(true)}
                  onBlur={() => conveyor.setTyping(false)}
                  required
                />
                {/* Guide 9.9: inside the field, 44px hit area, `aria-pressed`,
                    and the label itself says which way it is about to go. */}
                <button
                  type="button" className="signin-reveal"
                  aria-pressed={reveal} aria-controls="signin-password"
                  onClick={() => setReveal(v => !v)}
                >
                  {reveal ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-lg btn-primary signin-submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <button type="button" className="signin-link" onClick={() => setShowForgot(v => !v)}>
          Forgot password?
        </button>

        {/*
          **Self-service reset, built 10 September 2026**, the day the owner
          bought the domain. It replaced a message saying to go and ask an
          administrator, which is what OnHand said because there was nothing
          able to send an email.

          **It says the same thing whether the address is one we know or not**,
          and that is deliberate rather than unhelpful. A screen that says "no
          such account" lets anybody stand at the sign-in page and find out who
          has one, which is a list worth having if you are trying to get in.
          Supabase behaves the same way for the same reason.
        */}
        {showForgot && !sentTo && (
          <div className="signin-form">
            <div className="signin-msg signin-msg-info">
              Type your email above and OnHand will send you a link to set a new password.
            </div>
            {/* **Outlined, not blue.** The first build made this solid blue and
                the photograph showed two identical blue buttons stacked on each
                other. Two things of equal weight is the screen refusing to say
                which one you probably want, and the guide's section 0 allows one
                blue button per screen. Sign in is it. */}
            <button
              type="button" className="btn btn-outline signin-submit"
              disabled={sending || !email.trim()}
              onClick={sendReset}
            >
              {sending ? 'Sending...' : 'Send me a link'}
            </button>
          </div>
        )}

        {sentTo && (
          <div className="signin-msg signin-msg-info">
            {/* The space is explicit because JSX drops a newline that sits
                next to a tag, and "Check your email.If jake@..." is what that
                looks like on screen. */}
            <span className="signin-msg-title">Check your email.</span>{' '}
            If <b>{sentTo}</b> has an OnHand account, a link to set a new password is on
            its way. It works once and expires in an hour.
          </div>
        )}

        {/* One block, so the 22px between blocks does not open up between two
            lines that belong together.

            **Which build this browser is running**, on the second line. Added 7
            September 2026. An evening went into a fault that could not be
            diagnosed from outside, because there was no way to tell whether the
            screen in front of the person had the fix or the version before it.
            Two of the six wrong answers that evening turned on exactly that. */}
        <div className="signin-foot">
          <div className="signin-note">Need access? Ask your administrator.</div>
          <div className="signin-build">Build {BUILD}</div>
        </div>
      </div>
    </div>
  );
}
